import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "sonner";

import * as ozon from "@/api/ozonSupply";
import { extractErrorMessage } from "@/lib/api-errors";
import { applying, editing } from "./changeModel";
import { REGISTRY, dirKey, palletPlanKey } from "./ozonQueries";

/** Жизнь правки состава на экране: открыть, посмотреть план, применить, продолжить после сбоя.
 *
 *  ⚠️ Правда о заходе одна — поле `change` направления. Здесь только то, чего в нём нет: план
 *  каскада (его считают отдельной ручкой на каждый показ) и ЖИВОЙ шаг применения, пока каскад
 *  идёт: `apply` — один длинный запрос, а шаги коммитятся до каждого вызова наружу, и опрос
 *  `GET change` раз в две секунды — единственный способ показать, где он сейчас.
 */
export interface OzonChangeActions {
  /** План каскада; `undefined` — ещё не спрашивали или заход уже применяется */
  plan: ozon.OzonChangePlan | undefined;
  /** превью не посчиталось — текст отказа; `null` — всё в порядке */
  planError: string | null;
  planLoading: boolean;
  /** Заход с ЖИВЫМ шагом: пока каскад идёт, он свежее того, что в направлении. */
  change: ozon.OzonChange | null;
  start: UseMutationResult<ozon.OzonChange, unknown, void>;
  abort: UseMutationResult<ozon.OzonPlaces, unknown, void>;
  apply: UseMutationResult<ozon.OzonChange, unknown, void>;
  busy: boolean;
}

export const changeKey = (shipmentId: number): unknown[] => [
  "supply",
  "ozon",
  "change",
  shipmentId,
];

/** Ключ превью. ⚠️ В нём и заход, и МЕТКА ПРАВКИ раскладки: план — функция от текущих мест, и
 *  показанный из кеша он рассказывал бы про позавчерашнюю правку. */
export const previewKey = (
  shipmentId: number,
  changeId: number,
  stamp: number,
): unknown[] => [
  "supply",
  "ozon",
  "change-preview",
  shipmentId,
  changeId,
  stamp,
];

/** Ключ списка партий для «Заменить партию…». ⚠️ В нём та же МЕТКА ПРАВКИ раскладки, что и в
 *  `previewKey`: «свободно» считается за вычетом занятого НАШЕЙ раскладкой, и после каждой
 *  правки (`rebatch` / `remove` / `add`) прежние числа врут. Без метки список жил бы в кеше
 *  своей жизнью и предлагал партию, которой уже не осталось. */
export const batchesKey = (
  shipmentId: number,
  position: number,
  mpSku: string,
  stamp: number,
): unknown[] => [
  "supply",
  "ozon",
  "change-batches",
  shipmentId,
  position,
  mpSku,
  stamp,
];

export function useOzonChange(
  shipmentId: number,
  /** заход из направления — единственный ответ на вопрос «идёт ли правка» */
  change: ozon.OzonChange | null,
  opts: {
    /** положить восстановленную отменой раскладку — тем же путём, что и любая правка мест */
    put: (d: ozon.OzonPlaces) => void;
    /** перечитать направление: стадия, главная кнопка и сам `change` читаются только из него */
    afterLayoutChange: () => void;
    /** метка последней правки раскладки (`useOzonLayout.stamp`) */
    stamp: number;
    /** раздел «Что изменится» раскрыт: план считается ПО ЗАПРОСУ — он ходит в 1С за ценами */
    preview: boolean;
  },
): OzonChangeActions {
  const qc = useQueryClient();
  const id = change?.id ?? 0;

  // ⚠️ Объявлен ДО опроса: пока `apply` летит (а он летит минутами), его `isPending` —
  // единственное, по чему экран знает, что каскад идёт. Направление в это время молчит
  const apply = useMutation({
    mutationFn: () => ozon.applyOzonChange(shipmentId),
    onSuccess: (ch) => {
      qc.setQueryData(changeKey(shipmentId), ch);
      opts.afterLayoutChange();
      void qc.invalidateQueries({ queryKey: REGISTRY });
      void qc.invalidateQueries({ queryKey: dirKey(shipmentId) });
      // последний шаг каскада — план паллет портала склада (ревизия или записанный конфликт)
      void qc.invalidateQueries({ queryKey: palletPlanKey(shipmentId) });
      if (ch.state === "done")
        toast.success(
          ch.labels_to_reprint.positions.length > 0
            ? `Изменения применены. Перепечатайте этикетки новых коробов мест №${ch.labels_to_reprint.positions.join(", ")} (${ch.labels_to_reprint.codes} кодов)`
            : "Изменения применены",
        );
      // ⚠️ Сорвавшийся шаг — не «ошибка запроса»: ручка ответила 200 и рассказала, где встала.
      // Тост обязателен, иначе нажатие выглядит проигнорированным
      else if (ch.error)
        toast.error(`Каскад встал на шаге «${ch.state}»: ${ch.error}`);
    },
    onError: (e) => {
      // ⚠️ Оборвался ЗАПРОС, а не каскад: бэкенд идёт дальше по своему плану. Перечитать
      // направление обязаны — из него приедет шаг, на котором каскад сейчас, и опрос подхватит
      // прогресс. Без этого экран остался бы с «editing» на весь оставшийся каскад
      opts.afterLayoutChange();
      void qc.invalidateQueries({ queryKey: palletPlanKey(shipmentId) });
      failWith("Применить изменения не удалось")(e);
    },
  });

  // ⚠️ Живой шаг спрашиваем, пока каскад идёт — И пока летит сам `apply`. Второе обязательно:
  // «идёт ли каскад» в направлении появляется только после того, как `apply` ответил, а он
  // отвечает минутами — по одному `applying(change)` все эти минуты экран показывал бы превью
  // вместо прогресса, и «Продолжить» после сбоя выглядело бы проигнорированным.
  // Лишним опрос при этом не становится: вне применения он выключен.
  const live = useQuery({
    queryKey: changeKey(shipmentId),
    queryFn: () => ozon.getOzonChange(shipmentId),
    enabled: change !== null && (applying(change) || apply.isPending),
    refetchInterval: 2_000,
    // ⚠️ Заход кончился — ручка отвечает 404, и это не сбой связи: повторять нечего, надо
    // перечитать поставку (ниже). Повтор только оттягивал бы момент, когда экран скажет «готово»
    retry: false,
    gcTime: 0,
  });
  const fresh =
    live.data && change && live.data.id === change.id ? live.data : change;

  // каскад доехал до конца (или сорвался, или ручка ответила «захода нет») — перечитать поставку:
  // стадия, главная кнопка и напоминание о наклейках считаются по направлению, а не по заходу
  // ⚠️ `!apply.isPending`: первый опрос успевает застать заход ещё в `editing` (шаг
  // `editing → content` коммитится уже внутри `apply`), и без оговорки экран перечитывал бы
  // направление каждые две секунды всё применение
  const done =
    live.isError ||
    (live.data !== undefined && !applying(live.data) && !apply.isPending);
  useEffect(() => {
    if (done) opts.afterLayoutChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, live.dataUpdatedAt, live.errorUpdatedAt]);

  // ⚠️ План — только для захода В ПРАВКЕ: с первого шага каскад идёт по СОХРАНЁННОМУ плану
  // (`change.plan`), и посчитанный заново он рассказывал бы не про то, что сейчас происходит
  const planQ = useQuery({
    queryKey: previewKey(shipmentId, id, opts.stamp),
    queryFn: () => ozon.previewOzonChange(shipmentId),
    enabled: opts.preview && change !== null && editing(change),
    staleTime: 60_000,
    retry: false,
  });

  const start = useMutation({
    mutationFn: () => ozon.startOzonChange(shipmentId),
    onSuccess: () => {
      opts.afterLayoutChange();
      toast.success(
        "Режим изменений открыт: раскладка разморожена, наружу ничего не уйдёт до «Применить»",
      );
    },
    onError: failWith("Режим изменений не открылся"),
  });
  const abort = useMutation({
    mutationFn: () => ozon.abortOzonChange(shipmentId),
    onSuccess: (d) => {
      // ответ — ВОССТАНОВЛЕННАЯ раскладка: её экран и перерисовывает
      opts.put(d);
      toast.success(
        "Изменения отменены — раскладка вернулась к тому, что стоит на площадке",
      );
    },
    onError: failWith("Отменить изменения не удалось"),
  });
  return {
    plan: planQ.data,
    planError: planQ.isError
      ? extractErrorMessage(planQ.error, "План каскада не посчитался")
      : null,
    planLoading: planQ.isFetching,
    change: fresh,
    start,
    abort,
    apply,
    busy:
      start.isPending ||
      abort.isPending ||
      apply.isPending ||
      (fresh !== null && applying(fresh)),
  };
}

const failWith =
  (what: string) =>
  (e: unknown): void => {
    toast.error(extractErrorMessage(e, what));
  };
